import { Box, FormControl, IconButton, MenuItem, Select } from "@mui/material";
import { Typography } from "@mui/material";
import { shareModalStyles } from "./styles";
import CloseIcon from "@mui/icons-material/Close";
import { ContainerModal } from "../../pages/MyTracks/styles";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import {
  GetTrackVersionsResponse,
  Track,
  TrackVersion,
} from "../../proto/api_pb";
import { useCallback, useContext, useEffect, useState } from "react";
import {
  deleteTrackVersion,
  fetchTrackVersions,
  postTrack,
  postTrackVersion,
} from "../../services/trackService";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import {
  AuthContext,
  AuthContextInterface,
  UserDetailsContext,
} from "../../contexts";
import CustomButton from "../CustomButton/CustomButton";

export const getNumbersListInString = (numbers: number[]): string[] => {
  const options = ["Specify the version number"];
  const versionNumbers = numbers.map((number) => number.toFixed(1));
  return [...options, ...versionNumbers];
};

export const SelectVersionDeleteModal: React.FC<{
  isModalOpen: boolean;
  handleCloseModal: () => void;
  selectedTrack: Track | undefined;
  handleRefreshTracks: () => Promise<void>;
}> = ({
  isModalOpen,
  handleCloseModal,
  selectedTrack,
  handleRefreshTracks,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [versionNumberList, setVersionNumberList] = useState<number[]>([]);
  const { getValidAccessToken } = useGetValidAccessToken();
  const [trackVersionList, setTrackVersionList] = useState<TrackVersion[]>([]);
  const [selectedVNumber, setSelectedVNumber] = useState<number>(0);
  const [primaryVersion, setPrimaryVersion] = useState<TrackVersion>();
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const { isEnterpriseAccount, enterprise } = useContext(UserDetailsContext);
  const handleFetchTrackVersions = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = await getValidAccessToken();
      const id = selectedTrack?.getId();
      const response = (await fetchTrackVersions(
        token,
        id!
      )) as GetTrackVersionsResponse;

      setTrackVersionList(response.getTrackVersionsList());

      const versionNumberList = response
        .getTrackVersionsList()
        .filter((version) => version.getIsPrimary() !== true)
        .map((vr) => vr.getVersionNumber());

      const pv = response
        .getTrackVersionsList()
        .filter((version) => version.getIsPrimary() === true);

      if (pv.length > 0) {
        setPrimaryVersion(pv[0]);
      }

      setVersionNumberList(versionNumberList);
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  }, [getValidAccessToken, selectedTrack]);

  useEffect(() => {
    handleFetchTrackVersions();
  }, [handleFetchTrackVersions]);

  const handleChange = (event: any) => {
    setSelectedVNumber(event.target.value as number);
  };

  const handleDeletePrimary = async () => {
    const tv = trackVersionList.find(
      (version) =>
        Number(version.getVersionNumber()) === Number(selectedVNumber)
    );
    if (tv && primaryVersion) {
      try {
        setTrackVersionList([]);
        setIsLoading(true);
        const token = await getValidAccessToken();

        // set the new primary version
        const newPrimary = tv;
        newPrimary.setIsPrimary(true);
        await postTrackVersion(
          newPrimary,
          token,
          userId,
          isEnterpriseAccount ? enterprise?.id : undefined
        );
        if (selectedTrack) {
          selectedTrack.setTrackVersionId(newPrimary.getId());
          await postTrack(selectedTrack, await getValidAccessToken(), userId);
        }

        // delete the selected primary version
        await deleteTrackVersion(
          primaryVersion.getId(),
          token,
          isEnterpriseAccount ? enterprise?.id : undefined
        );

        handleRefreshTracks();
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
        handleCloseModal();
      }
    }
  };
  return (
    <ContainerModal
      open={isModalOpen}
      onClose={handleCloseModal}
      style={{ overflow: "auto" }}
    >
      <Box sx={{ ...shareModalStyles.mainContainer }}>
        <Box sx={shareModalStyles.topBarIconContainer}>
          <Box sx={shareModalStyles.shareIconContainer}>
            <DeleteOutlinedIcon />
          </Box>
          <Box sx={shareModalStyles.closeIconContainer}>
            <IconButton onClick={handleCloseModal}>
              <CloseIcon sx={{ fontSize: "30px" }} />
            </IconButton>
          </Box>
        </Box>
        <Typography sx={shareModalStyles.delteModalText}>
          You are about to delete the primary version
        </Typography>
        <Typography sx={shareModalStyles.delteModalText}>
          Select the version you want as a primary version:
        </Typography>
        <FormControl sx={{ margin: "20px 0 0" }} fullWidth>
          <Select value={selectedVNumber} displayEmpty onChange={handleChange}>
            {getNumbersListInString(versionNumberList).map((version) => (
              <MenuItem
                key={version}
                disabled={version === "Specify the version number"}
                value={version}
                sx={{ backgroundColor: "inherit" }}
              >
                {version}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={shareModalStyles.buttonsContainer}>
          <CustomButton
            text="Confirm"
            onClick={handleDeletePrimary}
            isDisabled={selectedVNumber === 0}
            loading={isLoading}
            customStyles={{ width: "48%" }}
          />
          <CustomButton
            text="Cancel"
            onClick={handleCloseModal}
            isSkipButton={true}
            customStyles={{ width: "48%" }}
          />
        </Box>
      </Box>
    </ContainerModal>
  );
};
